import { EventQueue } from './eventQueue';
import { Flusher } from './flusher';
import { loadSession, saveSession } from './sessionStore';
import type { SessionState } from './sessionStore';
import { LIMITS, QUEUE_KEY } from './types';
import type { SessionEndReason, TrackEvent, TrackerDeps } from './types';

interface ViewState {
  viewId: string;
  sessionId: string;
  sessionStartedAt: string;
  pathname: string;
  route: string;
  enteredAt: string;
  enteredMs: number;
}

/** A page the user is on while the tab is hidden: its view starts when the tab is visible again. */
interface ParkedView {
  pathname: string;
  route: string;
  fromRoute: string | null;
}

const iso = (ms: number): string => new Date(ms).toISOString();

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Turns router and visibility changes into page_view / page_leave / session_end events.
 *
 * A view exists only while the tab is visible, so its length is visible time. The tab
 * going hidden ends the view (that may be the last chance to send it); coming back
 * starts a new view of the same page. A session is one tab's run of activity: it
 * rotates after 30 idle minutes, and only sign-out and hand-off end it from here.
 * The page path is the only thing ever recorded: no query string, hash, id or token.
 */
export class Tracker {
  private session: SessionState | null;
  private view: ViewState | null = null;
  private parked: ParkedView | null = null;
  private lastRoute: string | null = null;
  private readonly deps: TrackerDeps;
  private readonly queue: EventQueue;
  private readonly flusher: Flusher;

  constructor(deps: TrackerDeps) {
    this.deps = deps;
    this.queue = new EventQueue(deps.storage, QUEUE_KEY);
    this.flusher = new Flusher(this.queue, deps.send);
    this.session = loadSession(deps.storage);
    this.flusher.schedule();
  }

  /**
   * The router is now at `pathname` (path only). Repeated calls for the same page do nothing; the same
   * path reporting a different outcome (the shared /success page) is a new page view.
   */
  onNavigate(pathname: string, kind?: unknown): void {
    const route = this.deps.matchRoute(pathname, kind);
    if (
      (this.view?.pathname === pathname && this.view.route === route) ||
      (this.parked?.pathname === pathname && this.parked.route === route)
    ) {
      return;
    }

    this.closeView();

    if (route === null || !this.canTrack()) {
      this.parked = null;
      this.lastRoute = null;
      return;
    }

    const fromRoute = this.lastRoute;
    if (!this.deps.isVisible()) {
      this.parked = { pathname, route, fromRoute };
      return;
    }
    this.parked = null;
    this.openView(pathname, route, fromRoute);
  }

  /** The tab became hidden (or is being unloaded) / visible again. */
  onVisibilityChange(visible: boolean): void {
    if (!visible) {
      if (this.view) {
        const { pathname, route } = this.view;
        this.closeView();
        this.parked = { pathname, route, fromRoute: route };
        this.lastRoute = route;
      }
      void this.flusher.flush(true);
      return;
    }

    const parked = this.parked;
    this.parked = null;
    if (parked && this.canTrack()) this.openView(parked.pathname, parked.route, parked.fromRoute);
  }

  /**
   * Ends the session: the last page_leave and the session_end go out together, in that
   * order, in one keepalive request. Resolves when it is sent or after `waitMs`, whichever
   * comes first, so a redirect is never held up for long.
   */
  endSession(reason: SessionEndReason, waitMs = 0): Promise<void> {
    const session = this.session;
    if (!session) return Promise.resolve();

    this.closeView();
    this.parked = null;
    this.enqueue({
      type: 'session_end',
      sessionId: session.id,
      sessionStartedAt: session.startedAt,
      reason,
      endedAt: iso(Date.now()),
    });
    this.session = null;
    this.lastRoute = null;
    saveSession(this.deps.storage, this.session);

    const sent = this.flusher.flush(true);
    return waitMs > 0 ? Promise.race([sent, sleep(waitMs)]) : sent;
  }

  dispose(): void {
    this.flusher.dispose();
  }

  private canTrack(): boolean {
    return this.deps.isAuthenticated() && !this.flusher.disabled;
  }

  private openView(pathname: string, route: string, fromRoute: string | null): void {
    const now = Date.now();
    this.rotateIfStale(now);

    let from = fromRoute;
    if (!this.session) {
      this.session = { id: this.deps.uuid(), startedAt: iso(now), lastActivity: now };
      from = null;
    }
    const session = this.session;
    session.lastActivity = now;

    const view: ViewState = {
      viewId: this.deps.uuid(),
      sessionId: session.id,
      sessionStartedAt: session.startedAt,
      pathname,
      route,
      enteredAt: iso(now),
      enteredMs: now,
    };
    this.view = view;
    this.lastRoute = route;
    saveSession(this.deps.storage, this.session);

    this.enqueue({
      type: 'page_view',
      viewId: view.viewId,
      sessionId: view.sessionId,
      sessionStartedAt: view.sessionStartedAt,
      route,
      ...(from ? { fromRoute: from } : {}),
      enteredAt: view.enteredAt,
    });
  }

  private closeView(): void {
    const view = this.view;
    if (!view) return;
    this.view = null;

    const now = Date.now();
    if (this.session) {
      this.session.lastActivity = now;
      saveSession(this.deps.storage, this.session);
    }
    this.enqueue({
      type: 'page_leave',
      viewId: view.viewId,
      sessionId: view.sessionId,
      sessionStartedAt: view.sessionStartedAt,
      enteredAt: view.enteredAt,
      durationMs: Math.max(0, Math.round(now - view.enteredMs)),
    });
  }

  private enqueue(event: TrackEvent): void {
    if (this.flusher.disabled) return;
    this.queue.push(event, Date.now());
    this.flusher.schedule();
  }

  private rotateIfStale(now: number): void {
    const session = this.session;
    if (!session) return;
    const idle = now - session.lastActivity > LIMITS.idleMs;
    const tooOld = now - Date.parse(session.startedAt) > LIMITS.maxSessionAgeMs;
    if (idle || tooOld) this.session = null;
  }
}
